import { make, commit } from "vuex-pathify";
import { Firebase } from "@/services/firebase";

// properties
const state = {
  items: {},
  resolved: [],
  getCache: false,
};

const mutations = {
  ...make.mutations(state),
};

const getters = {
  ...make.getters(state),
  lastModified(state) {
    let array = Object.values(state?.items || {});
    return (
      array.length &&
      array.reduce((max, item) => (max.votes > item.modified ? max : item))
        ?.modified
    );
  },
};

const actions = {
  // get tasks from cache
  // async fetchCachedTasks({ rootGetters, state }, ...{ done = false }) {
  //   let db = Firebase.firestore();
  //   let ref = await db
  //     .collection("teams")
  //     .doc(rootGetters["user/getTeam"])
  //     .collection("teamTasks")
  //     .where("list_id", "==", rootGetters["lists/currentId"])
  //     .where("status", "==", "published");

  //   if (!done) {
  //     ref.where("done", "==", false);
  //   }

  //   // always get cache since we'll realtime sync anything new
  //   let snapshot = await ref.get({ source: "cache" });

  //   // fetch from server if snapshot exists
  //   if (!snapshot.size) {
  //     console.log("Nothing cached, getting tasks from server...");
  //     snapshot = await ref.get({ source: "server" });
  //   } else {
  //     console.log("Getting tasks from cache...");
  //   }

  //   let items = {};
  //   (snapshot || []).forEach((item) => {
  //     items[item.id] = item.data();
  //   });

  //   state.items = items;
  // },

  /**
   * Bind tasks for realtime updates
   */
  async bindTasks({ dispatch, rootGetters, getters, state }) {
    let where = [
      ["status", "==", "published"], // newer than cache // not trashed
      ["list_id", "==", rootGetters["lists/currentId"]], // current list only
    ];

    if (!rootGetters["ui/showCompleted"]) {
      where.push(["done", "==", false]);
    }

    let response = await dispatch("openDBChannel", {
      clauses: {
        where,
        clauses: { limit: 100 },
      },
    });

    return response;
  },

  /**
   * Use batch process to increment counters
   */
  increments(
    { rootGetters, commit },
    { list_id, value = 1, type = "total", batch }
  ) {
    // update count in list
    batch.update(rootGetters["lists/dbRef"].doc(list_id), {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "lists/PATCH_DOC",
      {
        id: list_id,
        counts: {
          [type]: rootGetters["lists/items"]?.[list_id]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    // update count in project
    batch.update(rootGetters["project/dbRef"], {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "project/PATCH_DOC",
      {
        counts: {
          [type]: rootGetters["project/project"]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    // update count in team
    batch.update(rootGetters["team/dbRef"], {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "team/PATCH_DOC",
      {
        counts: {
          [type]: rootGetters["team/team"]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    return batch;
  },

  /**
   * Add a task
   */
  async addTask(
    { dispatch, rootGetters, getters, commit, state },
    { data, batch = null, shouldCommit = true }
  ) {
    // list_id required
    if (!data?.list_id) {
      throw new Error("You must provide a list_id with the task.");
    }

    // maybe start batch if not provided
    if (!batch) {
      let db = Firebase.firestore();
      batch = db.batch();
    }

    let doc = getters["dbRef"].doc();
    let insert = {
      list_id: data.list_id,
      done: false,
      status: "published",
      version: 1,
      created_at: new Date(),
      created_by: state._sync.userId,
      modified: Firebase.firestore.Timestamp.now().toMillis(),
      ...data,
    };

    // set new task in batch
    batch.set(doc, insert);
    commit("INSERT_DOC", {
      id: doc.id,
      ...insert,
    });

    // get list id
    batch = await dispatch("increments", {
      batch,
      list_id: data.list_id,
      value: 1,
      type: "total",
    });

    if (shouldCommit) {
      return await batch.commit();
    } else {
      return batch;
    }
  },

  async editTask({ dispatch }, { task, data }) {
    await dispatch("patch", {
      id: task.id,
      ...data,
    });
  },

  async toggleDone({ dispatch, getters, state, commit }, task) {
    let done = !task.done;

    let db = Firebase.firestore();
    let batch = db.batch();

    let insert = {
      done,
      updated_at: new Date(),
      updated_by: state._sync.userId,
    };

    batch.update(getters["dbRef"].doc(task.id), insert);
    commit("PATCH_DOC", {
      id: task.id,
      ...insert,
    });

    batch = await dispatch("increments", {
      batch,
      list_id: task.list_id,
      value: done ? 1 : -1,
      type: "done",
    });

    return await batch.commit();
  },

  // we move the task to the trashed collection
  async removeTask({ dispatch, getters, commit, state }, task) {
    let db = Firebase.firestore();
    let batch = db.batch();

    let insert = {
      status: "trashed",
      updated_at: new Date(),
      updated_by: state._sync.userId,
    };

    batch.update(getters["dbRef"].doc(task.id), insert);
    commit("PATCH_DOC", {
      id: task.id,
      ...insert,
    });

    batch = await dispatch("increments", {
      batch,
      list_id: task.list_id,
      value: -1,
    });

    if (task.done) {
      batch = await dispatch("increments", {
        batch,
        list_id: task.list_id,
        value: -1,
        type: "done",
      });
    }

    return await batch.commit();
  },
};

export default {
  firestorePath: "teams/{teamId}/teamTasks",
  firestoreRefType: "collection",
  moduleName: "tasks",
  statePropName: "items",
  sync: {
    defaultValues: {
      done: false,
      status: "published",
      version: 1,
      modified: Firebase.firestore.Timestamp.now().toMillis(), // need updated at by default
    },
    insertHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      return updateStore(doc);
    },
    patchHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      return updateStore(doc);
    },
    deleteHook: function(updateStore, id, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      return updateStore(id);
    },
  },
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
